import {debounce} from "../../utils/debounce";

interface FormSearchResult {
    label: string
    data: object
}

interface FormSearchField {
    single: boolean
    apiUrl: string
    isLoading: boolean
    readonly noResults: boolean
    searchQuery: string
    searchResults: FormSearchResult[]
    selected: string[]
    setupBasedOnSuggestions(suggestions: string): void
    performSearch: (...args: never[]) => void
    resultsProcessing: () => void
    emptySelected(): void
    clearResults(): void
    compareTerm(term: string, result: string): void
    selectResult(result: FormSearchResult): void
    readonly canCreateCustomTerm: boolean
    addCustomQuery(): void
    removeTerm(term: string): void
    popTermWithDelete(e: KeyboardEvent): void
    formatLabel(label: string): string
    selectMatchingTerm(event: FocusEvent): void
    selectMostSimilarTerm(): void
    handleDropdownOutsideClick(e: Event, dropdown: HTMLDetailsElement): void
    setUpDropdownListeners(e: Event): void
}

interface MapsApiResult {
    items: {
        name: string
        label: string
        type: string
        location: string
        zip: string
    }[]
    locality: string[]
}

interface MapsSearchResult {
    label: string
    data: {
        label: string
        location: string
        name: string
        position: { lon: number, lat: number }
        regionalStructure: {
            name: string
            type: string
        }[]
        type: string
        zip: string
    }
}

export default function (props: {saved: string, single: boolean}): FormSearchField {
    async function getSearchResults() {
        if (this.searchQuery === '') {
            this.clearResults()
            return
        }

        if (this.apiUrl === '' || this.searchQuery.length < 2) return

        this.isLoading = true
        await fetch(`${this.apiUrl}&query=${this.searchQuery}`)
            .then((response) => response.json())
            .then(this.resultsProcessing)
            .finally(() => { this.isLoading = false })
            .catch(() => this.searchResults = [])
    }

    return {
        single: props.single,
        apiUrl: '',
        isLoading: false,
        get noResults() {
            return this.apiUrl === '' ? false : !this.searchResults.length
        },
        searchQuery: props.single ? props.saved : '',
        searchResults: [],
        selected: !props.single ? (JSON.parse(props.saved) ?? []) : [],
        setupBasedOnSuggestions(suggestionsName) {
            if (suggestionsName === '') return

            const suggestions = suggestionsName.split(':')[0]
            let type = suggestionsName.split(':')[1] ?? ''

            switch (suggestions) {
                case 'maps':
                    const lang = document.documentElement.lang.slice(0, 2),
                        availableLanguages = ['cs', 'de', 'en', 'es', 'fr', 'pl', 'ru', 'sk']

                    let urlType = ''
                    if (type === 'city')
                        urlType = '&type=regional.municipality%2Cregional.municipality_part'
                    else if (type !== 'address')
                        urlType = type === ''
                            ? '&type=regional.municipality%2Cregional.municipality_part%2Cregional.address'
                            : `&type=regional.${type}`

                    this.apiUrl = `https://api.mapy.cz/v1/suggest?lang=${availableLanguages.includes(lang) ? lang : 'en'}&apikey=MtqBS19_yunyi_jpUbEX6inVnud978zFKtt3xw9owDI&limit=12${urlType}`
                    this.resultsProcessing = (result: MapsApiResult) => {
                        this.searchResults = result.items?.map(item => {
                            return { label: `${item.name}, ${item.location}`, data: item }
                        }) ?? []
                    }

                    if (type !== '') {
                        this.selectResult = (result: MapsSearchResult) => {
                            let allowedEditing = false

                            this.searchQuery = (type === 'address' && (result.data.type !== "regional.street" && result.data.type !== "regional.address"))
                                ? '' : result.data.name;

                            ['address', 'city', 'post_code', 'country'].forEach(resultType => {
                                if (!allowedEditing) {
                                    allowedEditing = type === resultType
                                    return
                                }

                                let typeValue = ''
                                switch (resultType) {
                                    case 'city':
                                        if (result.data.type === "regional.address") {
                                            const countryName = result.data.regionalStructure
                                                .find(regional => regional.type === "regional.country")?.name ?? ''

                                            typeValue = (result.data.location ?? '').replace(`, ${countryName}`, '')
                                        } else {
                                            typeValue = result.data.regionalStructure
                                                .find(regional => regional.type === "regional.municipality")?.name ?? ''
                                        }
                                        break;
                                    case 'post_code':
                                        typeValue = result.data.zip ?? ''
                                        break;
                                    case 'country':
                                        typeValue = result.data.regionalStructure
                                            .find(regional => regional.type === "regional.country")?.name ?? ''
                                        break;
                                }

                                if (typeValue !== '' && typeof typeValue === 'string') {
                                    const input = document.querySelector(`input[name="${resultType}"]`) as HTMLInputElement
                                    if (input) input.value = typeValue
                                }
                            })

                            this.clearResults()
                        }
                    }

                    break;
            }
        },
        performSearch: debounce(getSearchResults, 240),
        resultsProcessing: () => {},
        emptySelected() {
            this.selected = []
        },
        clearResults() {
            if (!this.single) this.searchQuery = ''
            this.searchResults = []
        },
        compareTerm(term, result) {
            return term.toLowerCase() === result.toLowerCase()
        },
        get canCreateCustomTerm() {
            return !this.selected.find((term: string) => this.compareTerm(term, this.searchQuery))
        },
        addCustomQuery() {
            this.searchQuery && this.selected.push(this.searchQuery) && this.clearResults()
        },
        selectResult(result: FormSearchResult) {
            this.single ? (this.searchQuery = result.label) : this.selected.push(result)
            this.clearResults()
        },
        removeTerm(term) {
            this.selected.splice(this.selected.findIndex((result: string) => this.compareTerm(term, result)), 1)
        },
        popTermWithDelete(e) {
            if (this.searchQuery.length || !["Delete", "Backspace"].includes(e.key)) return
            this.selected.pop()
        },
        formatLabel(label) {
            const regexp = new RegExp(`${this.searchQuery}`, 'i')
            return label?.replace(regexp, '<b>$&</b>') ?? ''
        },
        selectMatchingTerm(event) {
            if (this.single || this.$refs.fieldset.contains(event.relatedTarget as Node) || !this.searchQuery.length) return
            this.canCreateCustomTerm ? this.addCustomQuery() : this.clearResults()
        },
        selectMostSimilarTerm() {
            this.searchQuery.length && this.canCreateCustomTerm && this.addCustomQuery()
        },
        handleDropdownOutsideClick(e, dropdown) {
            if (dropdown.contains(e.target as HTMLElement)) return
            dropdown.open = false
        },
        setUpDropdownListeners(e) {
            const detail = e.target as HTMLDetailsElement
            document[detail.open ? "addEventListener" : "removeEventListener"]
                ('click', e => this.handleDropdownOutsideClick(e, detail))
        }
    }
}
